import { lazy, Suspense, useMemo } from 'react';
import { Refine, Authenticated } from '@refinedev/core';
import routerProvider, { NavigateToResource, CatchAllNavigate } from '@refinedev/react-router';
import { Route, Routes, Outlet, Navigate } from 'react-router';
import { ApolloProvider } from '@apollo/client/react';
import { App as AntdApp, ConfigProvider } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useNotificationProvider } from '@refinedev/antd';
import { AccessControlClient } from '@admin-panel/providers/lib/access/accesscontrolprovider';
import { LKAuthProvider } from '@admin-panel/providers/lib/authProvider';
import { shopifyApolloClient } from '@admin-panel/providers/lib/clients/shopifyAdminApi';
import { restDataProvider } from '@admin-panel/providers/lib/dataprovider';
import LKLayout from '@admin-panel/components/lib/Layout';
import { ThemeProvider } from '@admin-panel/config/theme';
import { PRODUCTION_FEATURES_VISIBLE } from '@admin-panel/config/env';
import { AccessControlConfig, getResources } from '@admin-panel/access-control';
import '@refinedev/antd/dist/reset.css';
import 'react-quill/dist/quill.snow.css';
import { PurchaseType } from '@admin-panel/types';
import { GlobalProvider, VendorGlobalProvider } from '@admin-panel/hooks';
import { store, persistor } from '@admin-panel/store';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { FoundationLoader } from '@admin-panel/ui';
import { lazyNamedExport } from '@admin-panel/utils';
import RouteAccessGuard from './RouteAccessGuard';

const ForegroundNotifications = lazy(() => import('./ForegroundNotifications'));
const PageNotFound = lazyNamedExport<{ reason?: 'unauthorized' | 'not_found' }>(
  () => import('@admin-panel/pages/PageNotFound'),
  'PageNotFound',
);

const BetterInventory = lazyNamedExport(
  () => import('@admin-panel/pages/BetterInventory'),
  'BetterInventory',
);
const StyleMetadataCorrections = lazyNamedExport(
  () => import('@admin-panel/pages/StyleMetadataCorrections'),
  'StyleMetadataCorrections',
);
const Login = lazyNamedExport<{ title: string }>(
  () => import('@admin-panel/pages/auth/login'),
  'Login',
);
const HomeDashboard = lazyNamedExport(
  () => import('@admin-panel/pages/dashboard'),
  'HomeDashboard',
);
const AssetUploadPage = lazyNamedExport(
  () => import('@admin-panel/pages/utility/AssetUploadPage'),
  'AssetUploadPage',
);
// The git-backed viewer takes over /utility/analytics-contract. The gateway-reading
// AnalyticsContractPage stays in the tree, just no longer routed.
const AnalyticsContractPage = lazyNamedExport(
  () => import('@admin-panel/pages/utility/ContractViewer'),
  'ContractViewer',
);
const AuditHistory = lazyNamedExport(
  () => import('@admin-panel/pages/utility/AuditHistory'),
  'AuditHistory',
);
const CrmManagementPage = lazyNamedExport(
  () => import('@admin-panel/pages/utility/CrmManagementPage'),
  'CrmManagementPage',
);
const DeeplinksPage = lazyNamedExport(
  () => import('@admin-panel/pages/utility/DeeplinksPage'),
  'DeeplinksPage',
);
const NotificationsPage = lazyNamedExport(
  () => import('@admin-panel/pages/utility/NotificationsPage'),
  'NotificationsPage',
);
const TargetsPage = lazyNamedExport(
  () => import('@admin-panel/pages/utility/TargetsPage'),
  'TargetsPage',
);
const LookbookStudioPage = lazyNamedExport(
  () => import('@admin-panel/pages/utility/LookbookStudio'),
  'LookbookStudioPage',
);
const DataAnalystSkillPage = lazyNamedExport(
  () => import('@admin-panel/pages/skills/DataAnalyst'),
  'DataAnalystSkillPage',
);
const BuildLookbook = lazyNamedExport(() => import('@admin-panel/pages/lookbook'), 'BuildLookbook');
const StoreImages = lazyNamedExport(() => import('@admin-panel/pages/StoreImages'), 'StoreImages');
const Metafields = lazy(() => import('@admin-panel/pages/Shopify/Metafields'));
const ShopifyCollections = lazy(() => import('@admin-panel/pages/Shopify/Collections'));
const ShopifyProducts = lazy(() => import('@admin-panel/pages/Shopify/Products'));
const PageBuilderPages = lazy(() => import('@admin-panel/pages/PageBuilder'));
const QuestionnaireProductDiscovery = lazy(
  () => import('@admin-panel/pages/questionnaire/ProductDiscovery'),
);
const QuestionnaireList = lazy(() => import('@admin-panel/pages/questionnaire/List'));

const ListERPUsers = lazyNamedExport(() => import('@admin-panel/pages/users'), 'ListERPUsers');
const ListVendorUsers = lazyNamedExport(
  () => import('@admin-panel/pages/users'),
  'ListVendorUsers',
);
const CreateVendorUser = lazyNamedExport(
  () => import('@admin-panel/pages/users'),
  'CreateVendorUser',
);
const ListCustomers = lazyNamedExport(() => import('@admin-panel/pages/users'), 'ListCustomers');
const CreateCustomer = lazyNamedExport(() => import('@admin-panel/pages/users'), 'CreateCustomer');
const ListStoreUsers = lazyNamedExport(() => import('@admin-panel/pages/users'), 'ListStoreUsers');
const CreateStoreUser = lazyNamedExport(
  () => import('@admin-panel/pages/users'),
  'CreateStoreUser',
);
const CreateWarehouseUser = lazyNamedExport(
  () => import('@admin-panel/pages/users'),
  'CreateWarehouseUser',
);
const EditERPUser = lazyNamedExport<{ userType: string }>(
  () => import('@admin-panel/pages/users'),
  'EditERPUser',
);
const ListB2BCustomers = lazyNamedExport(
  () => import('@admin-panel/pages/users'),
  'ListB2BCustomers',
);
const CreateB2BCustomer = lazyNamedExport(
  () => import('@admin-panel/pages/users'),
  'CreateB2BCustomer',
);
const EditB2BCustomer = lazyNamedExport(
  () => import('@admin-panel/pages/users'),
  'EditB2BCustomer',
);
const ShowB2BCustomer = lazyNamedExport(
  () => import('@admin-panel/pages/users'),
  'ShowB2BCustomer',
);

const ListStoreCredits = lazyNamedExport(
  () => import('@admin-panel/pages/storecredit'),
  'ListStoreCredits',
);
const CreditHistory = lazyNamedExport(
  () => import('@admin-panel/pages/CreditHistory'),
  'CreditHistory',
);
const VirtualTryOnsPage = lazyNamedExport(
  () => import('@admin-panel/pages/VirtualTryOns/VirtualTryOnsPage'),
  'VirtualTryOnsPage',
);
const VirtualTryOnGenerationDetailPage = lazyNamedExport(
  () => import('@admin-panel/pages/VirtualTryOns/VirtualTryOnGenerationDetailPage'),
  'VirtualTryOnGenerationDetailPage',
);
const ListVendors = lazyNamedExport(() => import('@admin-panel/pages/vendors'), 'ListVendors');
const EditVendor = lazyNamedExport(() => import('@admin-panel/pages/vendors'), 'EditVendor');
const CreateVendor = lazyNamedExport(() => import('@admin-panel/pages/vendors'), 'CreateVendor');
const VendorShow = lazyNamedExport(() => import('@admin-panel/pages/vendors'), 'VendorShow');
const DeleteSkus = lazyNamedExport(() => import('@admin-panel/pages/skus'), 'DeleteSkus');
const ListSkus = lazyNamedExport(() => import('@admin-panel/pages/skus'), 'ListSkus');
const CreateCategory = lazyNamedExport(
  () => import('@admin-panel/pages/categories'),
  'CreateCategory',
);
const CategoriesListing = lazyNamedExport(
  () => import('@admin-panel/pages/categories'),
  'CategoriesListing',
);
const ListFacilities = lazyNamedExport(
  () => import('@admin-panel/pages/facility'),
  'ListFacilities',
);
const CreateFacility = lazyNamedExport(
  () => import('@admin-panel/pages/facility'),
  'CreateFacility',
);
const EditFacility = lazyNamedExport(() => import('@admin-panel/pages/facility'), 'EditFacility');
const ShowFacility = lazyNamedExport(() => import('@admin-panel/pages/facility'), 'ShowFacility');
const EditSalesChannel = lazyNamedExport(
  () => import('@admin-panel/pages/saleschannel'),
  'EditSalesChannel',
);
const ListSalesChannels = lazyNamedExport(
  () => import('@admin-panel/pages/saleschannel'),
  'ListSalesChannels',
);

const ListDeliveryChallan = lazyNamedExport(
  () => import('@admin-panel/pages/dc'),
  'ListDeliveryChallan',
);
const CreateDeliveryChallan = lazyNamedExport<{ type: PurchaseType }>(
  () => import('@admin-panel/pages/dc'),
  'CreateDeliveryChallan',
);
const ShowDeliveryChallan = lazyNamedExport(
  () => import('@admin-panel/pages/dc'),
  'ShowDeliveryChallan',
);
const EditDeliveryChallan = lazyNamedExport(
  () => import('@admin-panel/pages/dc'),
  'EditDeliveryChallan',
);
const EditPurchaseOrder = lazyNamedExport(
  () => import('@admin-panel/pages/po'),
  'EditPurchaseOrder',
);
const ListPurchaseOrders = lazyNamedExport(
  () => import('@admin-panel/pages/po'),
  'ListPurchaseOrders',
);
const ListSkuAlbums = lazyNamedExport(
  () => import('@admin-panel/pages/skualbums'),
  'ListSkuAlbums',
);
const ListAIAlbums = lazyNamedExport(() => import('@admin-panel/pages/aialbums'), 'ListAIAlbums');
const ListWarehouseInventory = lazyNamedExport(
  () => import('@admin-panel/pages/inventory'),
  'ListWarehouseInventory',
);

const ListCustomerOrders = lazyNamedExport<{ includeTestOrders?: boolean }>(
  () => import('@admin-panel/pages/orders'),
  'ListCustomerOrders',
);
const OrderControlCenter = lazyNamedExport<{ view: string }>(
  () => import('@admin-panel/pages/orders'),
  'OrderControlCenter',
);
const ShowCustomerOrder = lazyNamedExport(
  () => import('@admin-panel/pages/orders'),
  'ShowCustomerOrder',
);
const ShowOrderPackage = lazyNamedExport(
  () => import('@admin-panel/pages/orders'),
  'ShowOrderPackage',
);
const CancelledAwbMovementExceptions = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/return'),
  'CancelledAwbMovementExceptions',
);
const ListReturnRequests = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/return'),
  'ListReturnRequests',
);
const ReturnApprovalsList = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/return'),
  'ReturnApprovalsList',
);
const ShowReturnRequest = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/return'),
  'ShowReturnRequest',
);
const ShowRefundRequest = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/refund'),
  'ShowRefundRequest',
);
const ShowOrderLine = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/show'),
  'ShowOrderLine',
);
const ListComments = lazyNamedExport(() => import('@admin-panel/pages/comments'), 'ListComments');
const DashOrders = lazy(() => import('@admin-panel/pages/orders/DashOrders'));
const OnholdOrders = lazyNamedExport(
  () => import('@admin-panel/pages/orders/OnHoldOrders'),
  'OnholdOrders',
);
const RefundsOnhold = lazyNamedExport(
  () => import('@admin-panel/pages/orders/RefundsOnhold'),
  'RefundsOnhold',
);
const PineLabsRecon = lazyNamedExport(
  () => import('@admin-panel/pages/orders/pinelabs-recon'),
  'PineLabsRecon',
);
const MTOTracker = lazyNamedExport(
  () => import('@admin-panel/pages/orders/MTOTracker'),
  'MTOTracker',
);
const CustomerActionTasks = lazyNamedExport(
  () => import('@admin-panel/pages/orders/CustomerActionTasks'),
  'CustomerActionTasks',
);
const PendingOrdersByBrand = lazyNamedExport(
  () => import('@admin-panel/pages/orders/PendingOrdersByBrand'),
  'PendingOrdersByBrand',
);

const CreateCoupon = lazyNamedExport(() => import('@admin-panel/pages/coupon'), 'CreateCoupon');
const EditCoupon = lazyNamedExport(() => import('@admin-panel/pages/coupon'), 'EditCoupon');
const ListCoupons = lazyNamedExport(() => import('@admin-panel/pages/coupon'), 'ListCoupons');
const CouponAnalyticsPage = lazyNamedExport(
  () => import('@admin-panel/pages/coupon'),
  'CouponAnalyticsPage',
);
const CreatePromotion = lazyNamedExport(
  () => import('@admin-panel/pages/promotion'),
  'CreatePromotion',
);
const ListPromotions = lazyNamedExport(
  () => import('@admin-panel/pages/promotion'),
  'ListPromotions',
);
const EditPromotion = lazyNamedExport(
  () => import('@admin-panel/pages/promotion'),
  'EditPromotion',
);
const ListVendorFacilities = lazyNamedExport(
  () => import('@admin-panel/pages/vendorfacility'),
  'ListVendorFacilities',
);
const ListJITInventory = lazyNamedExport(
  () => import('@admin-panel/pages/vendorinventory'),
  'ListJITInventory',
);

const CollectionsList = lazyNamedExport(
  () => import('@admin-panel/pages/collections'),
  'CollectionsList',
);
const CreateCollection = lazyNamedExport(
  () => import('@admin-panel/pages/collections'),
  'CreateCollection',
);
const EditCollection = lazyNamedExport(
  () => import('@admin-panel/pages/collections'),
  'EditCollection',
);
const ShowCollection = lazyNamedExport(
  () => import('@admin-panel/pages/collections'),
  'ShowCollection',
);
const CollectionImport = lazyNamedExport(
  () => import('@admin-panel/pages/collections'),
  'CollectionImport',
);
const CollectionSeoImport = lazyNamedExport(
  () => import('@admin-panel/pages/collections'),
  'CollectionSeoImport',
);
const SortStrategiesList = lazyNamedExport(
  () => import('@admin-panel/pages/sort-strategies'),
  'SortStrategiesList',
);
const CreateSortStrategy = lazyNamedExport(
  () => import('@admin-panel/pages/sort-strategies'),
  'CreateSortStrategy',
);
const EditSortStrategy = lazyNamedExport(
  () => import('@admin-panel/pages/sort-strategies'),
  'EditSortStrategy',
);
const ShowSortStrategy = lazyNamedExport(
  () => import('@admin-panel/pages/sort-strategies'),
  'ShowSortStrategy',
);
const ListStockTransfers = lazyNamedExport(
  () => import('@admin-panel/pages/stocktransfer'),
  'ListStockTransfers',
);
const EditStockTransfer = lazyNamedExport(
  () => import('@admin-panel/pages/stocktransfer'),
  'EditStockTransfer',
);
const CreateStockTransfer = lazyNamedExport(
  () => import('@admin-panel/pages/stocktransfer'),
  'CreateStockTransfer',
);
const AiStockTransfer = lazyNamedExport(
  () => import('@admin-panel/pages/stocktransfer'),
  'AiStockTransfer',
);
const GatepassOrderShow = lazyNamedExport(
  () => import('@admin-panel/pages/gatepassorder'),
  'GatepassOrderShow',
);
const LinkGatepassToOrder = lazyNamedExport(
  () => import('@admin-panel/pages/gatepassorder'),
  'LinkGatepassToOrder',
);

const WeeklyMerchOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/WeeklyMerchOverview'),
  'WeeklyMerchOverview',
);
const StoreBrandOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/StoreBrandOverview'),
  'StoreBrandOverview',
);

const MonthlyMerchKpisOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/MonthlyMerchKpisOverview'),
  'MonthlyMerchKpisOverview',
);
const DepthBreadthTrackerOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/DepthBreadthTrackerOverview'),
  'DepthBreadthTrackerOverview',
);
const BestsellersTrendsOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/BestsellersTrendsOverview'),
  'BestsellersTrendsOverview',
);
const StockTransferOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/StockTransferOverview'),
  'StockTransferOverview',
);
const MtoHealthOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/MtoHealthOverview'),
  'MtoHealthOverview',
);
const JitB2B2COverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/JitB2B2COverview'),
  'JitB2B2COverview',
);
const CatalogingHealthOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/CatalogingHealthOverview'),
  'CatalogingHealthOverview',
);
const StylistLeaderboardOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/StylistLeaderboardOverview'),
  'StylistLeaderboardOverview',
);
const StockAgeingOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/StockAgeingOverview'),
  'StockAgeingOverview',
);
const OnlinePerformanceOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/OnlinePerformanceOverview'),
  'OnlinePerformanceOverview',
);
const StockReconciliationOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/StockReconciliationOverview'),
  'StockReconciliationOverview',
);
const MerchScorecardOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/MerchScorecardOverview'),
  'MerchScorecardOverview',
);
const OpsMovementsOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/OpsMovementsOverview'),
  'OpsMovementsOverview',
);
const OpsStockOverview = lazyNamedExport(
  () => import('@admin-panel/pages/reports/OpsStockOverview'),
  'OpsStockOverview',
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
    },
    mutations: {
      retry: 0,
    },
  },
});

function App() {
  const resources = useMemo(() => getResources('ADMIN'), []);
  const accessControlClient = useMemo(() => new AccessControlClient(), []);

  return (
    <ReduxProvider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeProvider
          theme={{
            token: {
              colorPrimary: '#393AFB',
              colorBgLayout: '#ffffff',
              colorPrimaryBg: '#ffffff',
            },
          }}
        >
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: '#393AFB',
                colorBgLayout: '#ffffff',
                colorPrimaryBg: '#ffffff',
                fontFamily: '"Hanken Grotesk"',
                colorText: '#171717',
              },
            }}
          >
            <AntdApp>
              <QueryClientProvider client={queryClient}>
                <Refine
                  authProvider={LKAuthProvider}
                  accessControlProvider={accessControlClient.getAccessControlProvider()}
                  dataProvider={restDataProvider}
                  notificationProvider={useNotificationProvider}
                  routerProvider={routerProvider}
                  options={{
                    syncWithLocation: true,
                    warnWhenUnsavedChanges: true,
                    projectId: 'Unny6d-jT5kpU-QOXAra',
                  }}
                  resources={resources}
                >
                  <AccessControlConfig resources={resources} dataprovider={accessControlClient}>
                    <Routes>
                      <Route
                        element={
                          <Authenticated
                            key="authenticated-routes"
                            fallback={<CatchAllNavigate to="/login" />}
                          >
                            <GlobalProvider>
                              <VendorGlobalProvider>
                                {/* <LkNotificationsProvider> */}
                                <LKLayout>
                                  <MainOutlet />
                                </LKLayout>
                                {/* </LkNotificationsProvider> */}
                              </VendorGlobalProvider>
                            </GlobalProvider>
                          </Authenticated>
                        }
                      >
                        <Route index path="/" element={<HomeDashboard />} />
                        <Route path="/utility" element={<Outlet />}>
                          <Route path="assets">
                            <Route index path="" element={<AssetUploadPage />} />
                          </Route>
                          <Route path="deeplinks">
                            <Route index path="" element={<DeeplinksPage />} />
                          </Route>
                          <Route path="notifications">
                            <Route index path="" element={<NotificationsPage />} />
                          </Route>
                          <Route path="crm">
                            <Route index path="" element={<CrmManagementPage />} />
                          </Route>
                          <Route path="lookbook">
                            <Route index path="" element={<BuildLookbook />} />
                          </Route>
                          <Route path="lookbook-studio">
                            <Route index path="" element={<LookbookStudioPage />} />
                          </Route>
                          <Route path="store-images">
                            <Route index path="" element={<StoreImages />} />
                          </Route>
                          <Route path="pinelabs-recon">
                            <Route index path="" element={<PineLabsRecon />} />
                          </Route>
                          <Route path="audit-history">
                            <Route index path="" element={<AuditHistory />} />
                          </Route>
                          <Route path="targets">
                            <Route index path="" element={<TargetsPage />} />
                          </Route>
                          {PRODUCTION_FEATURES_VISIBLE ? (
                            <Route path="analytics-contract">
                              <Route index path="" element={<AnalyticsContractPage />} />
                            </Route>
                          ) : null}
                        </Route>

                        <Route path="/skills" element={<Outlet />}>
                          <Route path="data-analyst">
                            <Route index path="" element={<DataAnalystSkillPage />} />
                          </Route>
                        </Route>

                        <Route
                          path="/shopify"
                          element={
                            <ApolloProvider client={shopifyApolloClient}>
                              <Outlet />
                            </ApolloProvider>
                          }
                        >
                          <Route index path="" element={<Navigate to={`/shopify/metafields`} />} />
                          <Route path="metafields" element={<Metafields />} />
                          <Route
                            path="pages"
                            element={<Navigate to="/page-builder/pages" replace />}
                          />
                          <Route path="collections" element={<ShopifyCollections />} />
                          <Route path="products" element={<ShopifyProducts />} />
                        </Route>

                        <Route element={<Outlet />}>
                          <Route index element={null}></Route>
                          <Route path="customer">
                            <Route index path="" element={<ListCustomers />} />
                            <Route index path="new" element={<CreateCustomer />} />
                          </Route>
                          <Route path="b2b-customer">
                            <Route index path="" element={<ListB2BCustomers />} />
                            <Route index path="new" element={<CreateB2BCustomer />} />
                            <Route path="show/:id" element={<ShowB2BCustomer />} />
                            <Route index path="edit/:id" element={<EditB2BCustomer />} />
                          </Route>
                          <Route path="credit-history">
                            <Route path="" index element={<CreditHistory />} />
                          </Route>
                          <Route path="storecredits">
                            <Route path="" index element={<ListStoreCredits />} />
                          </Route>
                          <Route path="virtual-try-ons">
                            <Route path="" index element={<VirtualTryOnsPage />} />
                            <Route path=":id" element={<VirtualTryOnGenerationDetailPage />} />
                          </Route>
                          <Route path="orders">
                            <Route path="" index element={<ListCustomerOrders />} />
                            <Route path="show/:id" index element={<ShowCustomerOrder />} />
                          </Route>
                          <Route path="test-orders">
                            <Route
                              path=""
                              index
                              element={<ListCustomerOrders includeTestOrders />}
                            />
                          </Route>
                          <Route path="dash-orders" element={<DashOrders />} />
                          <Route
                            path="ordercontrolcenter"
                            element={<OrderControlCenter view="packages" />}
                          />
                          <Route path="on-hold-orders" element={<OnholdOrders />} />
                          <Route path="refunds-onhold" element={<RefundsOnhold />} />
                          <Route
                            path="pinelabs-recon"
                            element={<Navigate to="/utility/pinelabs-recon" replace />}
                          />
                          <Route path="customer-action-tasks" element={<CustomerActionTasks />} />
                          <Route
                            path="mtotracker"
                            element={<MTOTracker />}
                            // element={<OrderControlCenter view="mto" />}
                          />
                          <Route
                            path="mto-vendor-date-revisions"
                            element={
                              <Navigate to="/mtotracker?tab=vendor_date_revisions" replace />
                            }
                          />
                          <Route
                            path="pending-orders/:vendorId"
                            element={<PendingOrdersByBrand />}
                          />
                          <Route
                            path="b2b2ctracker"
                            element={<OrderControlCenter view="b2b2c" />}
                          />
                          <Route path="returns">
                            <Route path="" index element={<ListReturnRequests />} />
                            <Route path="approvals" element={<ReturnApprovalsList />} />
                            <Route
                              path="exceptions/cancelled-awb-movement"
                              element={<CancelledAwbMovementExceptions />}
                            />
                            <Route path="show/:id" index element={<ShowReturnRequest />} />
                          </Route>
                          <Route path="comments">
                            <Route path="" index element={<ListComments />} />
                          </Route>
                          <Route path="packages/show/:id" element={<ShowOrderPackage />} />
                          <Route path="lineitems/show/:id" element={<ShowOrderLine />} />
                          <Route path="refunds/show/:id" element={<ShowRefundRequest />} />
                        </Route>

                        <Route element={<Outlet />}>
                          <Route index element={null}></Route>
                          <Route path="/categories">
                            <Route index path="" element={<CategoriesListing />} />
                            <Route index path="new" element={<CreateCategory />} />
                          </Route>
                          <Route path="skualbums">
                            <Route index path="" element={<ListSkuAlbums />} />
                          </Route>
                          <Route path="aialbums">
                            <Route index path="" element={<ListAIAlbums />} />
                          </Route>
                          <Route path="inventory">
                            <Route index path="" element={<ListWarehouseInventory />} />
                          </Route>
                          <Route path="products">
                            <Route index path="" element={<BetterInventory />} />
                            <Route path="style-metadata" element={<StyleMetadataCorrections />} />
                          </Route>

                          <Route path="collections">
                            <Route index path="" element={<CollectionsList />} />
                            <Route path="import" element={<CollectionImport />} />
                            <Route path="import/seo" element={<CollectionSeoImport />} />
                            <Route path="new" element={<CreateCollection />} />
                            <Route path="edit/:id" element={<EditCollection />} />
                            <Route path="show/:id" element={<ShowCollection />} />
                          </Route>

                          <Route path="sort-strategies">
                            <Route index path="" element={<SortStrategiesList />} />
                            <Route path="new" element={<CreateSortStrategy />} />
                            <Route path="edit/:id" element={<EditSortStrategy />} />
                            <Route path="show/:id" element={<ShowSortStrategy />} />
                          </Route>

                          <Route path="/page-builder" element={<Outlet />}>
                            <Route index path="" element={<Navigate to="/page-builder/pages" />} />
                            <Route path="pages" element={<PageBuilderPages />} />
                            <Route path="pages/show/:handle" element={<PageBuilderPages />} />
                          </Route>

                          <Route path="questionnaire">
                            <Route
                              index
                              path=""
                              element={
                                <Suspense fallback={<div>Loading...</div>}>
                                  <QuestionnaireProductDiscovery />
                                </Suspense>
                              }
                            />
                            <Route
                              index
                              path=":productId"
                              element={
                                <Suspense fallback={<div>Loading...</div>}>
                                  <QuestionnaireList />
                                </Suspense>
                              }
                            />
                          </Route>
                        </Route>

                        <Route element={<Outlet />}>
                          <Route index element={null}></Route>
                          <Route path="/vendors">
                            <Route index path="" element={<ListVendors />} />
                            <Route path="new" element={<CreateVendor />} />
                            <Route path="show/:id" element={<VendorShow />} />
                            <Route path="edit/:id" element={<EditVendor />} />
                          </Route>
                          <Route path="/coupons">
                            <Route index path="" element={<ListCoupons />} />
                            <Route index path="new" element={<CreateCoupon />} />
                            <Route index path="edit/:id" element={<EditCoupon />} />
                            <Route index path=":id/analytics" element={<CouponAnalyticsPage />} />
                          </Route>
                          <Route path="/promotions">
                            <Route index path="" element={<ListPromotions />} />
                            <Route index path="new" element={<CreatePromotion />} />
                            <Route index path="edit/:id" element={<EditPromotion />} />
                          </Route>
                          <Route path="/skus">
                            <Route index path="" element={<ListSkus />} />
                            <Route path="delete" element={<DeleteSkus />} />
                          </Route>
                          <Route path="/vendorfacilities">
                            <Route index path="" element={<ListVendorFacilities />} />
                          </Route>
                          <Route path="/vendorinventory">
                            <Route index path="" element={<ListJITInventory />} />
                          </Route>
                        </Route>

                        <Route element={<Outlet />}>
                          <Route index element={null}></Route>
                          <Route path="deliverychallans">
                            <Route index path="" element={<ListDeliveryChallan />} />
                            <Route
                              index
                              path="new"
                              element={<CreateDeliveryChallan type={PurchaseType.DC} />}
                            />
                            <Route index path="show/:id" element={<ShowDeliveryChallan />} />
                            <Route index path="edit/:id" element={<EditDeliveryChallan />} />
                          </Route>
                          <Route path="purchaseorders">
                            <Route index path="" element={<ListPurchaseOrders />} />
                            <Route
                              index
                              path="new"
                              element={<CreateDeliveryChallan type={PurchaseType.PO} />}
                            />
                            <Route index path="show/:id" element={<ShowDeliveryChallan />} />
                            <Route index path="edit/:id" element={<EditPurchaseOrder />} />
                          </Route>
                          <Route path="stocktransfer">
                            <Route index path="" element={<ListStockTransfers />} />
                            <Route index path="new" element={<CreateStockTransfer />} />
                            <Route index path="edit/:id" element={<EditStockTransfer />} />
                          </Route>
                          <Route path="ai-stock-transfer" element={<AiStockTransfer />} />
                          <Route path="gatepass-orders">
                            <Route index path="show/:id" element={<GatepassOrderShow />} />
                            <Route path="link-gatepass/:id" element={<LinkGatepassToOrder />} />
                          </Route>
                        </Route>

                        <Route element={<Outlet />}>
                          <Route index element={null}></Route>
                          <Route path="weekly-merch-overview" element={<WeeklyMerchOverview />} />
                          <Route path="store-brand-overview" element={<StoreBrandOverview />} />
                          <Route
                            path="monthly-merch-kpis-overview"
                            element={<MonthlyMerchKpisOverview />}
                          />
                          <Route
                            path="depth-breadth-tracker-overview"
                            element={<DepthBreadthTrackerOverview />}
                          />
                          <Route
                            path="bestsellers-trends-overview"
                            element={<BestsellersTrendsOverview />}
                          />
                          <Route
                            path="stock-transfer-overview"
                            element={<StockTransferOverview />}
                          />
                          <Route path="mto-health-overview" element={<MtoHealthOverview />} />
                          <Route path="jit-b2b2c-overview" element={<JitB2B2COverview />} />
                          <Route
                            path="cataloging-health-overview"
                            element={<CatalogingHealthOverview />}
                          />
                          <Route
                            path="stylist-leaderboard"
                            element={<StylistLeaderboardOverview />}
                          />
                          <Route path="stock-ageing-overview" element={<StockAgeingOverview />} />
                          <Route
                            path="online-performance-overview"
                            element={<OnlinePerformanceOverview />}
                          />
                          <Route
                            path="stock-reconciliation-overview"
                            element={<StockReconciliationOverview />}
                          />
                          <Route path="merch-scorecard" element={<MerchScorecardOverview />} />
                          <Route path="ops-movements-overview" element={<OpsMovementsOverview />} />
                          <Route path="ops-stock-overview" element={<OpsStockOverview />} />
                        </Route>

                        <Route element={<Outlet />}>
                          <Route index element={null}></Route>
                          <Route path="/facilities">
                            <Route index path="" element={<ListFacilities />} />
                            <Route path="new" element={<CreateFacility />} />
                            <Route path="show/:id" element={<ShowFacility />} />
                            <Route path="edit/:id" element={<EditFacility />} />
                          </Route>
                          <Route path="/saleschannels">
                            <Route index path="" element={<ListSalesChannels />} />
                            <Route path="edit/:id" element={<EditSalesChannel />} />
                          </Route>
                          <Route path="/users" element={<Outlet />}>
                            <Route index element={null}></Route>
                            <Route path="erp">
                              <Route index path="" element={<ListERPUsers />} />
                              <Route path="new" element={<CreateWarehouseUser />} />
                              <Route path="edit/:id" element={<EditERPUser userType="admin" />} />
                            </Route>
                            <Route path="vendor">
                              <Route index path="" element={<ListVendorUsers />} />
                              <Route path="new" element={<CreateVendorUser />} />
                              <Route path="edit/:id" element={<EditERPUser userType="vendor" />} />
                            </Route>
                            <Route path="store">
                              <Route index path="" element={<ListStoreUsers />} />
                              <Route path="new" element={<CreateStoreUser />} />
                              <Route
                                path="edit/:id"
                                element={<EditERPUser userType="storestaff" />}
                              />
                            </Route>
                          </Route>
                        </Route>
                      </Route>

                      <Route
                        element={
                          <Authenticated key="auth-pages" fallback={<Outlet />}>
                            <NavigateToResource resource="dashboard" />
                          </Authenticated>
                        }
                      >
                        <Route
                          path="/login"
                          element={
                            <Suspense fallback={<FoundationLoader />}>
                              <Login title="Login to Foundation" />
                            </Suspense>
                          }
                        />
                      </Route>
                      <Route
                        element={
                          <Authenticated key="catch-all">
                            <GlobalProvider>
                              <VendorGlobalProvider>
                                <LKLayout>
                                  <Outlet />
                                </LKLayout>
                              </VendorGlobalProvider>
                            </GlobalProvider>
                          </Authenticated>
                        }
                      >
                        <Route
                          path="*"
                          element={
                            <Suspense fallback={<FoundationLoader />}>
                              <PageNotFound />
                            </Suspense>
                          }
                        />
                      </Route>
                    </Routes>
                  </AccessControlConfig>
                </Refine>
              </QueryClientProvider>
            </AntdApp>
          </ConfigProvider>
        </ThemeProvider>
      </PersistGate>
    </ReduxProvider>
  );
}

const MainOutlet = () => {
  return (
    <RouteAccessGuard>
      <Suspense fallback={null}>
        <ForegroundNotifications />
      </Suspense>
      <Suspense fallback={<FoundationLoader />}>
        <Outlet />
      </Suspense>
    </RouteAccessGuard>
  );
};

export default App;
